import { defer, EMPTY, from, iif, Observable, of, Subject, zip } from "rxjs";
import { catchError, concatMap, every, map, mergeMap, tap, toArray } from "rxjs/operators";
import { Injectable } from "@angular/core";
import { User } from "../../_objects/user";
import { UtilsService } from "../../_services/_utils.service";
import { CurrentUserDbService } from "../../_services/database/current-user-db.service";
import { IndividualReportTemplateDbService } from "../../_services/database/individual-report-template-db.service";
import { LoggedInUserDbService } from "../../_services/database/logged-in-user-db.service";
import { MarkDbService } from "../../_services/database/mark-db.service";
import { SubjectDbService } from "../../_services/database/subject-db.service";
import { SubjectAttributesDbService } from "../../_services/database/subject-attributes-db.service";
import { SubjectModelDbService } from "../../_services/database/subject-model-db.service";
import { SubjectModelLocationsDbService } from "../../_services/database/subject-model-locations-db.service";
import { TenantConfigurationDbService } from "../../_services/database/tenant-configuration-db.service";
import { TenantDetailDbService } from "../../_services/database/tenant-detail-db.service";
import { TitleDbService } from "../../_services/database/title-db.service";
import { UserDbService } from "../../_services/database/user-db.service";
import { UserProfileDbService } from "../../_services/database/user-profile-db.service";
import { FileSystemAccessService } from "../../_services/file-system-access.service";
import { MeUserService } from "../../_services/http-api/me-user.service";
import { FILE_SYSTEM } from "../electron.constants";
import { CurrentUserSchema } from "../models/current-user.schema";
import { IndividualReportTemplateSchema } from "../models/individual-report-template.schema";
import { LoggedInUserSchema } from "../models/logged-in-user.schema";
import { MarkSchema } from "../models/mark.schema";
import { ImagePath } from "../models/report-preview/report-template";
import { SubjectSchema } from "../models/subject.schema";
import { SubjectAttributesSchema } from "../models/subject-attributes.schema";
import { SubjectModelLocationsSchema } from "../models/subjectModel-locations.schema";
import { SubjectModelSchema } from "../models/subjectModel.schema";
import { TenantDetailSchema } from "../models/tenant-detail.schema";
import { TenantConfigSchema } from "../models/tenantConfiguration.schema";
import { TitleSchema } from "../models/title.schema";
import { UserProfileSchema } from "../models/user-profile.schema";
import { UserSchema } from "../models/user.schema";
import { OfflineUtilsService } from "./offline-utils.service";
import { IUserConfigSchema, UserConfigSchema } from "../models/userConfiguration.schema";
import { UserConfigurationDbService } from "../../_services/database/user-configuration-db.service";
import { BlobService } from "../../_services/blob.service";
import FILE_PREFIX = FILE_SYSTEM.PATH.FILE_PREFIX;
import { FeatureFlagService } from "../../_services/feature-flag/feature-flag.service";
import { AssetModuleClient } from "../../_services/asset-module/asset-module-client.service";
import { STANDALONE } from "../standalone-data/standalone-data";
import { LiveAssetStatus } from "@rvi/asset-modules";

@Injectable({
  providedIn: "root"
})
export class StandaloneDataService {
  constructor(
    private currentUserDbService: CurrentUserDbService,
    private fileSystemAccessService: FileSystemAccessService,
    private individualReportTemplateDbService: IndividualReportTemplateDbService,
    private loggedInUserDbService: LoggedInUserDbService,
    private markDbService: MarkDbService,
    private meUserService: MeUserService,
    private offlineUtilsService: OfflineUtilsService,
    private subjectDbService: SubjectDbService,
    private subjectAttributesDbService: SubjectAttributesDbService,
    private subjectModelDbService: SubjectModelDbService,
    private subjectModelLocationsDbService: SubjectModelLocationsDbService,
    private userDbService: UserDbService,
    private userProfileDbService: UserProfileDbService,
    private utilsService: UtilsService,
    private blobService: BlobService,
    private tenantConfigurationDbService: TenantConfigurationDbService,
    private tenantDetailDbService: TenantDetailDbService,
    private titleDbService: TitleDbService,
    private userConfigurationDbService: UserConfigurationDbService,
    private featureFlagService: FeatureFlagService,
    private assetModuleClient: AssetModuleClient
  ) {}

  public createStandaloneTenant(): Observable<boolean> {
    const subject: Subject<boolean> = new Subject<boolean>();

    this.createTenant()
      .pipe(
        mergeMap(() => this.createTenantConfig()),
        mergeMap(() => this.createUser()),
        mergeMap(() => this.createUserProfile()),
        mergeMap(() => this.createLoggedInUser()),
        mergeMap(() => this.meUserService.getMe()),
        mergeMap((x) => this.createCurrentUser(x)),
        mergeMap(() => this.createMark()),
        mergeMap(() => this.createTitle()),
        mergeMap(() => this.createIndividualReportTemplate()),
        mergeMap((x) => this.createIndividualReportTemplateImage(x)),
        mergeMap(() => this.createStandaloneSubjects())
      )
      .subscribe({
        next: () => {
          subject.next(true);
          subject.complete();
        },
        error: (e) => console.log(e)
      });

    return subject;
  }

  public createStandaloneSubjects(): Observable<unknown> {
    const flag = this.featureFlagService.isFlagEnabled("asset_module")();
    if (flag) {
      return this.createStandaloneLiveAssets();
    }

    return this.createStandaloneLegacySubjects();
  }

  private createStandaloneLiveAssets() {
    return this.importLiveAssetFromZipFile(STANDALONE.AVIATION_ENGINE_ZIP).pipe(
      concatMap(() => this.importLiveAssetFromZipFile(STANDALONE.INDUSTRIAL_GAS_TURBINE_ZIP)),
      concatMap(() => this.importLiveAssetFromZipFile(STANDALONE.WIND_TURBINE_GEARBOX_ZIP)),
      concatMap(() => this.linkReportTemplatesToAssetTypes()),
      tap(() => localStorage.setItem("first-visit-asset-module", new Date().toISOString())),
    );
  }

  private linkReportTemplatesToAssetTypes() {
    return this.assetModuleClient.getAssetTypes().pipe(
      map((assetTypes) => assetTypes.items.map((at) => at.id)),
      concatMap((assetTypeIds) => {
        const stringIds = JSON.stringify(assetTypeIds);
        const updatedTemplates = STANDALONE.INDIVIDUAL_REPORT_TEMPLATE_DATA.map((t) => ({
          ...t,
          subjectModelIds: stringIds,
          assetTypeIds: stringIds
        }));
        return this.individualReportTemplateDbService.putAll(updatedTemplates as IndividualReportTemplateSchema[]);
      })
    );
  }

  private createStandaloneLegacySubjects() {
    return this.createSubjectModel().pipe(
      mergeMap((x) => this.createSubjectModelImage(x)),
      mergeMap(() => this.createSubjectModelLocations()),
      mergeMap(() => this.createSubject()),
      mergeMap(() => this.createSubjectAttributes()),
      map(() => {})
    );
  }

  private importLiveAssetFromZipFile(zipFileName: string) {
    return this.loadAssetTypeZip(zipFileName).pipe(
      mergeMap((file) => this.assetModuleClient.importAssetType(file)),
      concatMap((id) => this.assetModuleClient.createLiveAsset(id)),
      concatMap((liveAsset) => this.setDefaultSerialNumber(liveAsset.id).pipe(map(() => liveAsset))),
      concatMap((liveAsset) => this.assetModuleClient.updateLiveAssetStatus(liveAsset.id, LiveAssetStatus.Operational, true)),
      catchError((e) => {
        console.log(e);
        return of({});
      })
    );
  }

  private setDefaultSerialNumber(liveAssetId: string) {
    return this.assetModuleClient.getLiveAssetProperties(liveAssetId).pipe(
      concatMap((properties) => {
        const serialNumber = properties.find((p) => p.name === "SerialNumber");
        if (!serialNumber) return of(undefined);
        return this.assetModuleClient.updateLiveAssetProperties(liveAssetId, [{ id: serialNumber.id, value: "default_1" }]);
      })
    );
  }

  private loadAssetTypeZip(zipFileName: string): Observable<File> {
    const path_separator: string = this.fileSystemAccessService.getSystemPathSeparator();
    return this.offlineUtilsService.getAppDirectory().pipe(
      mergeMap((appDirectoryPath) =>
        this.blobService.getBlobFromUrl(`${FILE_PREFIX}${appDirectoryPath}${path_separator}${STANDALONE.ASSETS_PATH}${path_separator}${zipFileName}`)
      ),
      map((blob) => new File([blob], zipFileName, { type: "application/zip" }))
    );
  }

  private createTenant(): Observable<TenantDetailSchema> {
    return this.tenantDetailDbService.put(STANDALONE.TENANT_DETAIL_DATA);
  }

  private createTenantConfig(): Observable<Array<TenantConfigSchema>> {
    return this.tenantConfigurationDbService.putAll(STANDALONE.TENANT_CONFIG_DATA);
  }

  private createUser(): Observable<Array<UserSchema>> {
    return this.userDbService.putAll(new Array<UserSchema>(STANDALONE.USER_DATA));
  }

  private createUserProfile(): Observable<UserProfileSchema> {
    return this.userProfileDbService.put(STANDALONE.USER_PROFILE_DATA);
  }

  private createLoggedInUser(): Observable<LoggedInUserSchema> {
    return this.loggedInUserDbService.put(STANDALONE.LOGGED_IN_USER_DATA);
  }

  private createCurrentUser(user: User): Observable<CurrentUserSchema> {
    const currentUserSchema: CurrentUserSchema = new CurrentUserSchema(user);

    return this.currentUserDbService.put(currentUserSchema);
  }

  private createSubjectModel(): Observable<Array<SubjectModelSchema>> {
    const subjectModelSchemas: Array<SubjectModelSchema> = new Array<SubjectModelSchema>();
    subjectModelSchemas.push(STANDALONE.AVIATION_ENGINE_SUBJECT_MODEL_DATA as unknown as SubjectModelSchema);
    subjectModelSchemas.push(STANDALONE.INDUSTRIAL_GAS_TURBINE_SUBJECT_MODEL_DATA as unknown as SubjectModelSchema);
    subjectModelSchemas.push(STANDALONE.WIND_TURBINE_GEARBOX_SUBJECT_MODEL_DATA as unknown as SubjectModelSchema);
    return this.subjectModelDbService.putAll(subjectModelSchemas);
  }

  private createSubjectModelImage(subjectModelSchemas: Array<SubjectModelSchema>): Observable<boolean> {
    let appDirectoryPath: string = "";
    const path_separator: string = this.fileSystemAccessService.getSystemPathSeparator();
    const filteredSchemas = subjectModelSchemas.filter((schema) => schema.structImageFilePath !== null);

    return this.offlineUtilsService.getAppDirectory().pipe(
      tap((x) => (appDirectoryPath = x as string)),
      mergeMap(() => filteredSchemas),
      mergeMap((x) =>
        zip(
          of(x),
          defer(() => {
            return this.blobService.getBlobFromUrl(
              `${FILE_PREFIX}${appDirectoryPath}${path_separator}${STANDALONE.ASSETS_PATH}${path_separator}${x.structImageFilePath}`
            );
          })
        )
      ),
      mergeMap((x) => {
        const subjectModelSchema: SubjectModelSchema = x[0];
        const blob: Blob = x[1];
        const fileName: string = subjectModelSchema.structImageFilePath.split(path_separator).pop();
        const file: File = new File([blob], fileName, { type: this.utilsService.getMimeType(fileName) });

        return defer(() => this.fileSystemAccessService.saveFile(file, subjectModelSchema.structImageFilePath));
      }),
      every((x) => x)
    );
  }

  private createSubjectModelLocations(): Observable<Array<SubjectModelLocationsSchema>> {
    const subjectModelLocationsSchemas: Array<SubjectModelLocationsSchema> = new Array<SubjectModelLocationsSchema>();
    subjectModelLocationsSchemas.push(STANDALONE.AVIATION_ENGINE_SUBJECT_MODEL_LOCATIONS_DATA);
    subjectModelLocationsSchemas.push(STANDALONE.INDUSTRIAL_GAS_TURBINE_SUBJECT_MODEL_LOCATIONS_DATA);
    subjectModelLocationsSchemas.push(STANDALONE.WIND_TURBINE_GEARBOX_SUBJECT_MODEL_LOCATIONS_DATA);
    return this.subjectModelLocationsDbService.putAll(subjectModelLocationsSchemas);
  }

  private createSubject(): Observable<Array<SubjectSchema>> {
    const subjectSchemas: Array<SubjectSchema> = new Array<SubjectSchema>();
    subjectSchemas.push(STANDALONE.AVIATION_ENGINE_SUBJECT_DATA as unknown as SubjectSchema);
    subjectSchemas.push(STANDALONE.INDUSTRIAL_GAS_TURBINE_SUBJECT_DATA as unknown as SubjectSchema);
    subjectSchemas.push(STANDALONE.WIND_TURBINE_GEARBOX_SUBJECT_DATA as unknown as SubjectSchema);
    return this.subjectDbService.putAll(subjectSchemas);
  }

  private createSubjectAttributes(): Observable<Array<SubjectAttributesSchema>> {
    const subjectAttributesSchemas: Array<SubjectAttributesSchema> = new Array<SubjectAttributesSchema>();
    subjectAttributesSchemas.push(STANDALONE.AVIATION_ENGINE_SUBJECT_ATTRIBUTES_DATA);
    subjectAttributesSchemas.push(STANDALONE.INDUSTRIAL_GAS_TURBINE_SUBJECT_ATTRIBUTES_DATA);
    subjectAttributesSchemas.push(STANDALONE.WIND_TURBINE_GEARBOX_SUBJECT_ATTRIBUTES_DATA);
    return this.subjectAttributesDbService.putAll(subjectAttributesSchemas);
  }

  private createMark(): Observable<Array<MarkSchema>> {
    return this.markDbService.putAll(STANDALONE.MARK_DATA);
  }

  private createTitle(): Observable<Array<TitleSchema>> {
    return this.titleDbService.putAll(STANDALONE.TITLE_DATA);
  }

  public createIndividualReportTemplate(): Observable<Array<IndividualReportTemplateSchema>> {
    let individualReportTemplateSchema: Array<IndividualReportTemplateSchema>;
    from(STANDALONE.INDIVIDUAL_REPORT_TEMPLATE_DATA)
      .pipe(
        map((x) => {
          return x as unknown as IndividualReportTemplateSchema;
        }),
        toArray()
      )
      .subscribe((x) => {
        individualReportTemplateSchema = x;
      });

    return this.individualReportTemplateDbService.putAll(individualReportTemplateSchema);
  }

  private createIndividualReportTemplateImage(individualReportTemplateSchemas: Array<IndividualReportTemplateSchema>): Observable<boolean> {
    let appDirectoryPath: string = "";
    const path_separator: string = this.fileSystemAccessService.getSystemPathSeparator();
    return this.offlineUtilsService.getAppDirectory().pipe(
      tap((x) => (appDirectoryPath = x as string)),
      mergeMap(() => individualReportTemplateSchemas),
      mergeMap((x) => JSON.parse(x.imagePathList) as Array<ImagePath>),
      mergeMap((x) =>
        zip(
          of(x),
          defer(() => {
            return this.blobService.getBlobFromUrl(
              `${FILE_PREFIX}${appDirectoryPath}${path_separator}${STANDALONE.ASSETS_PATH}${path_separator}${x.src}`
            );
          })
        )
      ),
      mergeMap((x) => {
        const imagePath: ImagePath = x[0];
        const blob: Blob = x[1];

        const fileName: string = imagePath.src.split(path_separator).pop();
        const file: File = new File([blob], fileName, { type: this.utilsService.getMimeType(fileName) });

        return defer(() => this.fileSystemAccessService.saveFile(file, imagePath.src));
      }),
      every((x) => x)
    );
  }

  private createUserConfig(): Observable<Array<IUserConfigSchema>> {
    let userConfigSchemas: Array<IUserConfigSchema>;
    from(STANDALONE.USER_CONFIG_DATA)
      .pipe(
        map((x) => {
          return x as unknown as UserConfigSchema;
        }),
        toArray()
      )
      .subscribe((x) => {
        userConfigSchemas = x;
      });

    return this.userConfigurationDbService.putAll(userConfigSchemas);
  }
}
